import { Component, createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { sendWebSocketMessage, webSocketServerStatus } from "../../../../../utils/websocket";
import AdminPanel from "../../../../../components/AdminPanel";
import { useI18n } from "../../../../../i18n";
import { getServer } from "../../../../../utils/utils";

const ServerInformationsStatus: Component = () => {
  const { t } = useI18n();
    const [server] = createSignal(getServer());

  onMount(() => {
    const intervalId = setInterval(() => {
      sendWebSocketMessage("server_status", { serverID: server().id });
    }, 2000);

    onCleanup(() => clearInterval(intervalId));
  });

  const [isValidWSConnection, setIsValidWSConnection] = createSignal(webSocketServerStatus().isWebSocketConnected);
  const [isValidHTTPRequest, setIsValidHTTPRequest] = createSignal(
    webSocketServerStatus().lastRequest.getTime() > new Date().getTime() - 1000 * 60,
  );
  const [isValidVersion, setIsValidVersion] = createSignal(
    webSocketServerStatus().versionComparator == 0 || webSocketServerStatus().versionComparator == -1,
  );

  createEffect(() => {
    setIsValidWSConnection(webSocketServerStatus().isWebSocketConnected);
    setIsValidHTTPRequest(webSocketServerStatus().lastRequest.getTime() > new Date().getTime() - 1000 * 60);
    setIsValidVersion(
      webSocketServerStatus().versionComparator == 0 || webSocketServerStatus().versionComparator == -1,
    );
  }, [webSocketServerStatus()]);

  return (
    <>
      <AdminPanel
        title={t("dashboard.server.server_informations.connection_status_title", "Connection Status")}
        description={t(
          "dashboard.server.server_informations.connection_status_description",
          "Here you can see the connection status of your server.",
        )}
        type="none"
      >
        <div class="join join-horizontal border-b border-base-200 rounded-none">
          <div class="join-item h-32 flex flex-col items-center border-r border-base-200">
            <div class="join-title w-36 font-semibold text-center p-2 border-b border-base-200">
              {t("dashboard.server.server_informations.http_request_title", "HTTP Request")}
            </div>
            <div class="flex-1 flex items-center justify-center">
              <i
                class={`fa-solid fa-arrows-down-to-line ${isValidHTTPRequest() ? "text-success" : "text-error"} text-3xl`}
              ></i>
            </div>
          </div>
          <div class="join-item p-4 text-base-content/60">
            <Show
              when={isValidHTTPRequest()}
              fallback={
                <p>
                  {t(
                    "dashboard.server.server_informations.http_request_fallback_part1",
                    "We haven't received any request from your server in the last minute, make sure to have installed the",
                  )}{" "}
                  <a href="/workshop" class="link">
                    {t("dashboard.server.server_informations.workshop_content", "workshop content")}
                  </a>{" "}
                  {t(
                    "dashboard.server.server_informations.http_request_fallback_part2",
                    "correctly and have fully configured your server id & token, read the",
                  )}{" "}
                  <a href="/docs/getting-started/configuration" class="link">
                    {t("dashboard.server.server_informations.documentation", "documentation")}
                  </a>{" "}
                  {t("dashboard.server.server_informations.http_request_fallback_part3", "for more information.")}
                </p>
              }
            >
              <p>
                {t(
                  "dashboard.server.server_informations.http_request_success",
                  "Congratulations your server is correctly setup you can now configure it.",
                )}
              </p>
            </Show>
          </div>
        </div>

        <div class="join join-horizontal border-b border-base-200 rounded-none">
          <div class="join-item h-32 flex flex-col items-center border-r border-base-200">
            <div class="join-title w-36 font-semibold text-center p-2 border-b border-base-200">
              {t("dashboard.server.server_informations.websocket_title", "WebSocket")}
            </div>
            <div class="flex-1 flex items-center justify-center">
              <i class={`fa-solid fa-right-left ${isValidWSConnection() ? "text-success" : "text-error"} text-3xl`}></i>
            </div>
          </div>
          <div class="join-item p-4 text-base-content/60">
            <Show
              when={isValidWSConnection()}
              fallback={
                <p>
                  {t(
                    "dashboard.server.server_informations.websocket_fallback_part1",
                    "Your server is not connected to the websocket, advanced features are not available, to use them you need to install the",
                  )}{" "}
                  <a href="/docs/getting-started/installation#dll" class="link">
                    {t("dashboard.server.server_informations.gwsocket_module", "GWSocket Module")}
                  </a>{" "}
                  {t("dashboard.server.server_informations.websocket_fallback_part2", "on your server.")}
                </p>
              }
            >
              <p>
                {t(
                  "dashboard.server.server_informations.websocket_success",
                  "Congratulations you successfully connected to your server in websocket, advanced features are now available.",
                )}
              </p>
            </Show>
          </div>
        </div>

        <div class="join join-horizontal rounded-none">
          <div class="join-item h-32 flex flex-col items-center border-r border-base-200">
            <div class="join-title w-36 font-semibold text-center p-2 border-b border-base-200">
              {t("dashboard.server.server_informations.version_title", "Version")}
            </div>
            <div class="flex-1 flex items-center justify-center">
              <i
                class={`fa-solid fa-tag ${!isValidHTTPRequest() ? "text-warning" : isValidVersion() ? "text-success" : "text-error"} text-3xl`}
              ></i>
            </div>
          </div>
          <div class="join-item p-4 text-base-content/60">
            <Show
              when={isValidHTTPRequest()}
              fallback={
                <p>
                  {t(
                    "dashboard.server.server_informations.version_fallback_http_part1",
                    "We can't check the version of your server, make sure to have installed the",
                  )}{" "}
                  <a href="/workshop" class="link">
                    {t("dashboard.server.server_informations.workshop_content", "workshop content")}
                  </a>{" "}
                  {t(
                    "dashboard.server.server_informations.version_fallback_http_part2",
                    "correctly and have fully configured your server id & token, read the",
                  )}{" "}
                  <a href="/docs/getting-started/configuration" class="link">
                    {t("dashboard.server.server_informations.documentation", "documentation")}
                  </a>{" "}
                  {t("dashboard.server.server_informations.version_fallback_http_part3", "for more information.")}
                </p>
              }
            >
              <Show
                when={isValidVersion()}
                fallback={
                  <p>
                    {t(
                      "dashboard.server.server_informations.version_fallback_outdated",
                      "Your server is outdated, new features will not work correctly, you need to update it. If you are using the Workshop version simply restart your server and it will automatically update. But if you are using the github repo or a uncompressed workshop version you need to manually update it.",
                    )}
                  </p>
                }
              >
                <p>
                  {t(
                    "dashboard.server.server_informations.version_success",
                    "Great your server is up to date and will work perfectly with the latest features",
                  )}
                </p>
              </Show>
            </Show>
          </div>
        </div>
      </AdminPanel>
    </>
  );
};

export default ServerInformationsStatus;
